import type { Image, Link, RichText } from '@eneco/cms-types';
import { asRichText } from '@eneco/cms-types';
import { createImageUrlBuilder } from '@sanity/image-url';

import type {
  LocalizedArrayEntry,
  PortableTextBlock,
  PortableTextSpan,
  SanityImage,
  SanityImageWithAlt,
  SanityLink,
} from './sanity-types';

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/**
 * `@sanity/image-url` THROWS on a ref that doesn't match its expected shape
 * (`image-<id>-<w>x<h>-<fmt>`), rather than returning gracefully — so this
 * validates and extracts width/height up front. A ref failing this check
 * means `toImage` returns `undefined` without ever calling the builder,
 * matching the "never throw" adapter contract.
 */
const parseAssetRef = (ref: string): { width: number; height: number } | undefined => {
  const match = /^image-[a-zA-Z0-9]+-(\d+)x(\d+)-[a-zA-Z]+$/.exec(ref);
  if (!match) return undefined;
  return { width: Number(match[1]), height: Number(match[2]) };
};

/**
 * Alt text from either shape the migration bundle uses: `Image`'s internationalized array (Eneco's
 * `alt` text plugin) or `focalPointImage`'s plain string (`HeroCard.brandPrimaryImage`,
 * `ContentCard.image`, …). NOTE (simplification): for the array shape, picks the first non-empty entry
 * rather than the requesting locale's — `toImage` has no locale parameter on the shared `CmsAdapter`
 * contract. Fine while pages are fetched one-locale-at-a-time; revisit if a page ever mixes alt text
 * across languages.
 */
const firstLocalizedValue = (entries: unknown): string | undefined => {
  if (typeof entries === 'string') return entries.length > 0 ? entries : undefined;
  return Array.isArray(entries)
    ? (entries as LocalizedArrayEntry[]).find(entry => Boolean(entry?.value))?.value
    : undefined;
};

export interface ImageMapperConfig {
  projectId: string;
  dataset: string;
}

export const createToImage = ({ projectId, dataset }: ImageMapperConfig) => {
  const builder = createImageUrlBuilder({ projectId, dataset });

  return (raw: unknown): Image | undefined => {
    if (!isObject(raw)) return undefined;
    // Accept either a bare Sanity image ({ asset }) or Eneco's `imageWithAlt`
    // wrapper ({ image: { asset }, alt: [...] }).
    const wrapper = raw as SanityImageWithAlt;
    const image = (isObject(wrapper.image) ? wrapper.image : raw) as SanityImage;
    const ref = image.asset?._ref;
    if (typeof ref !== 'string' || ref.length === 0) return undefined;
    const dimensions = parseAssetRef(ref);
    if (!dimensions) return undefined;

    const src = builder.image({ asset: { _ref: ref } }).url();
    // Both levels, because both shapes exist in the dataset. `imageWithAlt` models alt as a
    // sibling of `image`; the migration bundle's own `image` object carries its own `alt` instead,
    // and the MKB home page uses that one exclusively — every image there rendered `alt=""`.
    //
    // Not only an accessibility bug. Sanity's documented way to make an image click-to-editable is
    // stega inside the `alt` attribute (there is no text node in an `<img>` to hide it in), so an
    // empty alt is also an image the editor cannot click in Presentation.
    const alt = firstLocalizedValue(wrapper.alt) ?? firstLocalizedValue((image as Record<string, unknown>).alt);
    const { width, height } = dimensions;

    return {
      src,
      ...(alt ? { alt } : {}),
      width,
      height,
      aspectRatio: `${width}/${height}`,
    };
  };
};

/**
 * Maps Eneco's `link` object type. `internalLink` arrives already resolved to
 * `{ _type, slug }` by the GROQ projection (see `DEFAULT_BLOCK_PROJECTIONS` in
 * `queries.ts`) — no async resolution here, per the eager-hydration contract.
 *
 * NOTE (simplification): the internal href is built as a bare `/<slug>`. Real
 * routing likely needs a `_type`-based path prefix (e.g. `/producten/<slug>`
 * vs `/artikelen/<slug>`) — content/routing team input needed before this is
 * production-correct.
 */
const nonEmpty = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;

/**
 * Destination of an internal link, from whichever shape resolved it.
 *
 * `reference` is v3's internal target and `internalLink` the POC's; both arrive already projected
 * to `{ slug }` by GROQ.
 *
 * `reference` used to yield nothing — v3 pointed every internal link at `migrationUnresolvedReference`,
 * a stub carrying only `label`/`note` (ADR-0049), with real targets deferred (ADR-0044). A later
 * migration run repointed them at page documents, so this branch now resolves. An individual link can
 * still carry no reference at all (`linkType: 'internal'` with the field absent), which stays
 * `undefined` on purpose: see `toLink`'s note on why an internal link never falls back to `href`.
 */
const internalHref = (link: SanityLink): string | undefined => {
  const slug = nonEmpty(link.reference?.slug) ?? nonEmpty(link.internalLink?.slug);
  return slug ? `/${slug.replace(/^\/+/, '')}` : undefined;
};

/**
 * Label for an internal link that carries no `text` of its own, taken from the target page.
 *
 * Needed because the migration left `text` off some internal links — the GB mega menu's top-level
 * entries, for instance — while setting it on others in the same document. The alternative is rendering
 * the raw path as the label.
 *
 * `browserTitle` is an SEO title, so it usually ends in the site suffix editors append
 * (`"About us | Eneco UK"`). Everything from the first `|` is dropped. That is a heuristic, not a
 * contract: it is only ever reached when the link has no real text, so the worst case is a slightly
 * long label rather than a wrong destination. A link's own `text` always wins.
 */
const referenceLabel = (link: SanityLink): string | undefined => {
  const title = nonEmpty(link.reference?.browserTitle) ?? nonEmpty(link.internalLink?.browserTitle);
  return title ? nonEmpty(title.split('|')[0]) : undefined;
};

export const toLink = (raw: unknown): Link | undefined => {
  // A bare URL string, which the in-repo migration engine wrote for a General Link even though the
  // v3 schema declares an object. The engine has since been removed; the branch stays because it
  // costs nothing and keeps any such link rendering.
  const bare = typeof raw === 'string' ? nonEmpty(raw) : undefined;
  if (typeof raw === 'string') return bare ? { href: bare } : undefined;

  if (!isObject(raw)) return undefined;
  const link = raw as SanityLink;

  // An explicit 'internal' is restricted to its reference — falling back to the URL field would
  // silently turn an unresolved internal link into whatever stale href the migration left behind.
  // Everything else ('external'/'anchor'/'mailto'/'tel', or no linkType at all) reads the URL
  // field first: `href` in v3, `externalUrl` in the POC schema.
  const href =
    link.linkType === 'internal'
      ? internalHref(link)
      : nonEmpty(link.href) ?? nonEmpty(link.externalUrl) ?? internalHref(link);
  if (href === undefined) return undefined;

  const text = nonEmpty(link.text) ?? referenceLabel(link);

  return {
    href,
    ...(text ? { text } : {}),
    ...(link.openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {}),
    ...(link.linkType ? { linkType: link.linkType } : {}),
  };
};

const escapeHtml = (text: string): string =>
  text.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');

const escapeAttr = (value: string): string =>
  escapeHtml(value).replaceAll('"', '&quot;');

type MarkDef = NonNullable<PortableTextBlock['markDefs']>[number];

/**
 * Render a span. A mark is either a decorator (`strong`/`em`) or a `_key`
 * referencing an entry in the block's `markDefs` (e.g. a `link`). Link marks
 * become `<a>` tags with an escaped href; unknown marks are passed through.
 */
const renderSpan = (span: PortableTextSpan, markDefs: MarkDef[]): string => {
  // `htmlToBlocks` (block-tools) turns a source `<br>` into a literal `\n` inside a span's text
  // rather than a `hardBreak` mark, so it must be restored here or it gets whitespace-collapsed away.
  const text = escapeHtml(span.text ?? '').replaceAll('\n', '<br>');
  const marks = span.marks ?? [];
  return marks.reduce((acc, mark) => {
    if (mark === 'strong') return `<strong>${acc}</strong>`;
    if (mark === 'em') return `<em>${acc}</em>`;
    const def = markDefs.find(d => d._key === mark);
    if (def?._type === 'link' && typeof def.href === 'string' && def.href.length > 0) {
      return `<a href="${escapeAttr(def.href)}">${acc}</a>`;
    }
    return acc;
  }, text);
};

const renderBlock = (block: PortableTextBlock): string => {
  const markDefs = block.markDefs ?? [];
  const inner = (block.children ?? []).map(child => renderSpan(child, markDefs)).join('');
  const style = block.style ?? 'normal';
  if (style.startsWith('h') && style.length === 2) return `<${style}>${inner}</${style}>`;
  if (block.listItem) return `<li>${inner}</li>`;
  return `<p>${inner}</p>`;
};

/** Portable Text has no list container — each item is a sibling block carrying `listItem`. Emitting
 * them bare produces `<li>`s with no parent: invalid HTML, and browsers render them unstyled. So a
 * run of consecutive items of the same kind is wrapped once, and the wrapper closes as soon as the
 * kind changes or a non-list block appears.
 *
 * NOTE: `level` (nested lists) is not handled — a sub-list renders flat, inside its parent's
 * wrapper. That is still better-formed than today's output, and nesting is best fixed by moving to
 * `@portabletext/react` rather than growing this serializer (see `toRichText`'s note). */
const listTagFor = (block: PortableTextBlock): 'ol' | 'ul' | undefined => {
  if (!block.listItem) return undefined;
  return block.listItem === 'number' ? 'ol' : 'ul';
};

/** A plain paragraph — not a heading, not a list item. */
const isParagraph = (block: PortableTextBlock): boolean => {
  const style = block.style ?? 'normal';
  return !block.listItem && !(style.startsWith('h') && style.length === 2);
};

/**
 * Separator between consecutive paragraphs.
 *
 * The design system renders `<p>` as `<Text as="p">` with **no margin**
 * (`@eneco/components/RichText/RichText`), so paragraphs sit flush and read as one block of text.
 * Sitecore content only looks right because authors typed literal `<br><br>` between them; the
 * HTML→Portable Text conversion drops those, leaving nothing to space migrated paragraphs apart.
 * Reproducing them here keeps the two lanes visually identical, which is what the `?_cms=` comparison
 * is for.
 *
 * The better fix is a margin on `Paragraph` in the shared `RichText`, but that component also serves
 * the Sitecore lane, whose content still carries its own `<br>`s — adding one there would double the
 * spacing on every existing page. Worth doing when that lane goes.
 */
const PARAGRAPH_SEPARATOR = '<br><br>';

const renderBlocks = (blocks: PortableTextBlock[]): string => {
  let html = '';
  let openList: 'ol' | 'ul' | undefined;
  let previous: PortableTextBlock | undefined;

  for (const block of blocks) {
    const tag = listTagFor(block);
    if (openList && openList !== tag) {
      html += `</${openList}>`;
      openList = undefined;
    }
    if (tag && !openList) {
      html += `<${tag}>`;
      openList = tag;
    }
    if (previous && isParagraph(previous) && isParagraph(block)) html += PARAGRAPH_SEPARATOR;
    html += renderBlock(block);
    previous = block;
  }

  return openList ? `${html}</${openList}>` : html;
};

/**
 * Serialize Portable Text into the HTML string `RichText` brand the rest of the
 * codebase expects.
 *
 * NOTE (A.5 finding): `RichText` is a branded *string*; Sanity rich text is a
 * *block array*. Serializing here is lossy — it cannot express custom Portable
 * Text components (callouts, embeds). Recommend widening `RichText` to
 * `string | PortableTextBlock[]` in Phase A.5 / I rather than serializing.
 */
export const toRichText = (raw: unknown): RichText | undefined => {
  // Already-serialized HTML string (e.g. a legacy/migrated field) passes through.
  if (typeof raw === 'string') return raw.length > 0 ? asRichText(raw) : undefined;
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const blocks = raw.filter(
    (b): b is PortableTextBlock => isObject(b) && (b as { _type?: string })._type === 'block',
  );
  if (blocks.length === 0) return undefined;
  const html = renderBlocks(blocks);
  return html.length > 0 ? asRichText(html) : undefined;
};
